import { useEffect, useMemo, useState } from "react";
import { Calendar, dateFnsLocalizer, Views } from "react-big-calendar";
import {
  format, parse, startOfWeek, getDay, addDays, addMonths, addHours,
  startOfMonth, endOfMonth, startOfDay, endOfDay,
} from "date-fns";
import { es } from "date-fns/locale/es";
import "react-big-calendar/lib/css/react-big-calendar.css";
import {
  CalendarDays, RefreshCw, AlertCircle, History, X, Trash2, Plus,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const locales = { es };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (d) => startOfWeek(d, { weekStartsOn: 1 }),
  getDay,
  locales,
});

const messages = {
  allDay: "Todo el día", previous: "Anterior", next: "Siguiente", today: "Hoy",
  month: "Mes", week: "Semana", day: "Día", agenda: "Agenda",
  date: "Fecha", time: "Hora", event: "Evento", noEventsInRange: "Sin eventos en este rango",
  showMore: (t) => `+${t} más`,
};

/**
 * Vista calendario general (multi-técnico).
 * - `embedded`        widget compacto (Dashboard).
 * - `dashboardMode`   vista DIA fija 10:00-20:00 sin scroll (Dashboard).
 */
export default function CalendarView({ embedded = false, dashboardMode = false }) {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState("calendar");        // "calendar" | "history"
  const [view, setView] = useState(dashboardMode ? Views.DAY : embedded ? Views.WEEK : Views.MONTH);
  const [date, setDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [filterTech, setFilterTech] = useState("");
  const [connected, setConnected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);      // event obj o "new"
  const [newSlot, setNewSlot] = useState(null);      // { start, end }

  const range = useMemo(() => {
    let start, end;
    if (view === Views.DAY) { start = startOfDay(date); end = endOfDay(date); }
    else if (view === Views.WEEK) {
      const ws = startOfWeek(date, { weekStartsOn: 1 });
      start = startOfDay(ws); end = endOfDay(addDays(ws, 6));
    } else {
      start = startOfDay(addDays(startOfMonth(date), -7));
      end = endOfDay(addDays(endOfMonth(date), 7));
    }
    return { start: start.toISOString(), end: end.toISOString() };
  }, [view, date]);

  const load = async () => {
    setLoading(true);
    try {
      const [techsR, evsR] = await Promise.all([
        api.get("/google/technicians"),
        api.get("/google/events", { params: { start: range.start, end: range.end, technician_id: filterTech } }),
      ]);
      setTechnicians(techsR.data.technicians || []);
      setConnected(evsR.data.connected);
      const evlist = (evsR.data.events || []).map((e) => ({
        id: e.id,
        title: e.title,
        start: new Date(e.start),
        end: new Date(e.end),
        allDay: e.all_day,
        resource: e,
      }));
      setEvents(evlist);
    } catch {
      toast.error("No se pudo cargar el calendario");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range.start, range.end, filterTech]);

  const eventStyleGetter = (ev) => ({
    style: {
      backgroundColor: ev.resource.color || "#6E8B3D",
      borderColor: ev.resource.color || "#6E8B3D",
      color: "#fff",
      borderRadius: 0,
      border: "none",
      fontSize: "11px",
      padding: "2px 4px",
    },
  });

  if (connected === false) {
    return (
      <div className="ds-card p-8 text-center">
        <AlertCircle size={32} className="text-[#525252] mx-auto mb-3" />
        <h3 className="font-bold text-lg mb-2">Google Calendar no conectado</h3>
        <p className="text-sm text-[#525252] mb-4">
          Conecta la cuenta corporativa de Google desde Configuración para ver los eventos.
        </p>
        <a
          href="/settings?tab=google_calendar"
          className="inline-flex items-center gap-2 bg-[#6E8B3D] text-white px-4 py-2 text-sm hover:bg-[#556C2E]"
        >
          <CalendarDays size={14} /> Ir a Configuración
        </a>
      </div>
    );
  }

  // Vista Dashboard: día fijo 10:00 → 20:00 sin scroll
  const dashboardHours = dashboardMode ? {
    min: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 10, 0, 0),
    max: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 20, 0, 0),
  } : {};

  return (
    <div className={embedded ? "" : "space-y-3"}>
      {!embedded && (
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D]">// Vista global</div>
            <h1 className="font-display text-3xl md:text-4xl font-black mt-1">Calendario</h1>
            <p className="text-xs text-[#525252] mt-1">
              Eventos de todos los técnicos (cuenta única) · {technicians.length} calendarios activos
            </p>
          </div>
          <div className="flex gap-2 items-end">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block mb-1">Filtrar técnico</label>
              <select
                value={filterTech}
                onChange={(e) => setFilterTech(e.target.value)}
                data-testid="calendar-filter-tech"
                className="ds-card px-3 py-2 text-sm"
              >
                <option value="">Todos</option>
                {technicians.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <button
              onClick={() => setEditing("new")}
              data-testid="calendar-new-btn"
              className="bg-[#6E8B3D] text-white px-3 py-2 text-xs uppercase tracking-wider inline-flex items-center gap-1 hover:bg-[#556C2E]"
            >
              <Plus size={12} /> Nueva cita
            </button>
            <button onClick={load} className="border border-[#E5E5E0] px-3 py-2 text-xs font-mono uppercase hover:bg-[#F7F7F4] inline-flex items-center gap-1" data-testid="calendar-refresh">
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Recargar
            </button>
          </div>
        </div>
      )}

      {!embedded && (
        <div className="flex gap-2 border-b border-[#E5E5E0]">
          <button
            type="button"
            onClick={() => setTab("calendar")}
            data-testid="calendar-tab-calendar"
            className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border-b-2 ${tab === "calendar" ? "border-[#111110] text-[#111110]" : "border-transparent text-[#525252]"}`}
          >
            Calendario
          </button>
          {isAdmin() && (
            <button
              type="button"
              onClick={() => setTab("history")}
              data-testid="calendar-tab-history"
              className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border-b-2 inline-flex items-center gap-1.5 ${tab === "history" ? "border-[#111110] text-[#111110]" : "border-transparent text-[#525252]"}`}
            >
              <History size={12} /> Historial
              <span className="text-[8px] bg-[#111110] text-white px-1">solo admin</span>
            </button>
          )}
        </div>
      )}

      {tab === "calendar" && (
        <div
          className="ds-card p-3"
          style={{ height: dashboardMode ? 480 : embedded ? 380 : 720, overflow: dashboardMode ? "hidden" : undefined }}
        >
          <Calendar
            localizer={localizer}
            events={events}
            messages={messages}
            culture="es"
            view={view}
            date={date}
            onView={setView}
            onNavigate={setDate}
            views={dashboardMode ? [Views.DAY] : [Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
            step={30}
            timeslots={2}
            eventPropGetter={eventStyleGetter}
            startAccessor="start"
            endAccessor="end"
            style={{ height: "100%" }}
            {...dashboardHours}
            selectable={!dashboardMode}
            onSelectSlot={(slot) => {
              if (embedded) return;
              setNewSlot({ start: slot.start, end: addHours(slot.start, 1) });
              setEditing("new");
            }}
            onSelectEvent={(ev) => {
              if (embedded) return;
              setEditing(ev);
            }}
          />
        </div>
      )}

      {tab === "history" && isAdmin() && <HistoryView />}

      {!embedded && technicians.length > 0 && tab === "calendar" && (
        <div className="flex flex-wrap gap-2 pt-1">
          {technicians.map((t) => (
            <div key={t.id} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-[#E5E5E0] bg-white">
              <span className="w-3 h-3 rounded-full" style={{ background: t.google_calendar_color || "#6E8B3D" }} />
              {t.name}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EventDialog
          mode={editing === "new" ? "create" : "edit"}
          ev={editing === "new" ? null : editing}
          technicians={technicians}
          presetStart={newSlot?.start}
          presetEnd={newSlot?.end}
          onClose={() => { setEditing(null); setNewSlot(null); }}
          onSaved={() => { setEditing(null); setNewSlot(null); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Diálogo crear/editar cita ----------

function toLocalISO(date) {
  // ISO local con offset (formato Google: "2026-06-23T10:00:00+02:00")
  const pad = (n) => String(n).padStart(2, "0");
  const tzMin = -date.getTimezoneOffset();
  const sign = tzMin >= 0 ? "+" : "-";
  const tzH = pad(Math.floor(Math.abs(tzMin) / 60));
  const tzM = pad(Math.abs(tzMin) % 60);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:00${sign}${tzH}:${tzM}`;
}

function toLocalInput(date) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function EventDialog({ mode, ev, technicians, presetStart, presetEnd, onClose, onSaved }) {
  const initialCal = ev?.resource?.calendar_id || technicians[0]?.google_calendar_id || "";
  const initialStart = ev?.start || presetStart || new Date();
  const initialEnd = ev?.end || presetEnd || addHours(initialStart, 1);
  const [calendarId, setCalendarId] = useState(initialCal);
  const [title, setTitle] = useState(ev?.title || "");
  const [description, setDescription] = useState(ev?.resource?.description || "");
  const [startStr, setStartStr] = useState(toLocalInput(initialStart));
  const [endStr, setEndStr] = useState(toLocalInput(initialEnd));
  const [busy, setBusy] = useState(false);

  const originalCalendarId = ev?.resource?.calendar_id || "";

  const submit = async () => {
    if (!calendarId) { toast.error("Selecciona un técnico/calendario"); return; }
    if (!title.trim()) { toast.error("El título es obligatorio"); return; }
    const startD = new Date(startStr);
    const endD = new Date(endStr);
    if (!(endD > startD)) { toast.error("La hora de fin debe ser posterior al inicio"); return; }
    setBusy(true);
    try {
      if (mode === "create") {
        await api.post("/google/events", {
          calendar_id: calendarId,
          title: title.trim(),
          description: description.trim(),
          start: toLocalISO(startD),
          end: toLocalISO(endD),
        });
        toast.success("Cita creada");
      } else {
        await api.patch(`/google/events/${ev.id}`, {
          calendar_id: originalCalendarId,
          new_calendar_id: calendarId !== originalCalendarId ? calendarId : "",
          title: title.trim(),
          description: description.trim(),
          start: toLocalISO(startD),
          end: toLocalISO(endD),
        });
        toast.success("Cita actualizada");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("¿Cancelar esta cita? Se eliminará de Google Calendar.")) return;
    setBusy(true);
    try {
      await api.delete(`/google/events/${ev.id}`, { params: { calendar_id: originalCalendarId } });
      toast.success("Cita cancelada");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[#111110] shadow-hard w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E0]">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D]">// Calendario</div>
            <div className="font-display font-bold text-sm">{mode === "create" ? "Nueva cita" : "Editar cita"}</div>
            {ev?.resource?.repair_id && (
              <a href={`/repairs/${ev.resource.repair_id}`} className="text-[11px] text-[#6E8B3D] underline" data-testid="event-repair-link">
                Fecha estimada de la reparación {ev.resource.repair_code} · al mover o cancelar esta cita se actualiza la reparación
              </a>
            )}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F7F4]"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3">
          <Field label="Técnico / Calendario">
            <select
              value={calendarId}
              onChange={(e) => setCalendarId(e.target.value)}
              data-testid="event-tech"
              className="w-full ds-card px-2 py-1.5 text-sm"
            >
              <option value="">— Seleccionar —</option>
              {technicians.map((t) => (
                <option key={t.id} value={t.google_calendar_id}>{t.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Título *">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="event-title"
              className="w-full ds-card px-2 py-1.5 text-sm"
            />
          </Field>
          <Field label="Descripción">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              data-testid="event-description"
              className="w-full ds-card px-2 py-1.5 text-sm"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Inicio">
              <input
                type="datetime-local"
                value={startStr}
                onChange={(e) => setStartStr(e.target.value)}
                data-testid="event-start"
                className="w-full ds-card px-2 py-1.5 text-sm"
              />
            </Field>
            <Field label="Fin">
              <input
                type="datetime-local"
                value={endStr}
                onChange={(e) => setEndStr(e.target.value)}
                data-testid="event-end"
                className="w-full ds-card px-2 py-1.5 text-sm"
              />
            </Field>
          </div>
        </div>
        <div className="flex items-center gap-2 px-4 py-3 border-t border-[#E5E5E0]">
          {mode === "edit" && (
            <button
              type="button"
              disabled={busy}
              onClick={cancel}
              data-testid="event-cancel-btn"
              className="px-3 py-2 text-xs uppercase tracking-wider border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center gap-1"
            >
              <Trash2 size={12} /> Cancelar cita
            </button>
          )}
          <div className="flex-1" />
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-2 text-xs uppercase tracking-wider border border-[#E5E5E0] hover:bg-[#F7F7F4]"
          >
            Cerrar
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={submit}
            data-testid="event-save-btn"
            className="px-3 py-2 text-xs uppercase tracking-wider bg-[#6E8B3D] text-white hover:bg-[#556C2E] disabled:opacity-50"
          >
            {mode === "create" ? "Crear" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252] block mb-1">{label}</label>
      {children}
    </div>
  );
}

// ---------- Historial (solo admin) ----------

function HistoryView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/google/events/history", { params: { limit: 300 } })
      .then((r) => setItems(r.data.items || []))
      .catch(() => toast.error("No se pudo cargar el historial"))
      .finally(() => setLoading(false));
  }, []);

  const fmtVal = (v) => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };
  const actionLabel = { created: "Creada", updated: "Modificada", deleted: "Cancelada" };

  if (loading) return <div className="text-sm text-[#525252] p-4">Cargando historial...</div>;
  if (items.length === 0) {
    return (
      <div className="ds-card p-8 text-center text-sm text-[#525252]">
        Sin entradas en el historial.
      </div>
    );
  }
  return (
    <div className="ds-card overflow-x-auto" data-testid="calendar-history-list">
      <table className="w-full text-xs">
        <thead className="bg-[#FAFAF7] border-b border-[#E5E5E0]">
          <tr>
            <Th>Fecha y hora</Th>
            <Th>Usuario</Th>
            <Th>Calendario</Th>
            <Th>Evento</Th>
            <Th>Acción</Th>
            <Th>Campo</Th>
            <Th>Antes</Th>
            <Th>Después</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-b border-[#E5E5E0] hover:bg-[#FAFAF7]">
              <Td mono>{new Date(it.ts).toLocaleString("es")}</Td>
              <Td>{it.user_name}</Td>
              <Td>{it.calendar_name}</Td>
              <Td>{it.event_title || "—"}</Td>
              <Td>
                <span className="inline-block px-1.5 py-0.5 bg-[#F7F7F4] border border-[#E5E5E0] text-[10px] uppercase">
                  {actionLabel[it.action] || it.action}
                </span>
              </Td>
              <Td mono>{it.field}</Td>
              <Td mono>{fmtVal(it.before)}</Td>
              <Td mono>{fmtVal(it.after)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }) {
  return <th className="px-2 py-2 text-left font-mono text-[9px] uppercase tracking-widest text-[#525252]">{children}</th>;
}
function Td({ children, mono }) {
  return <td className={`px-2 py-1.5 align-top ${mono ? "font-mono text-[10px]" : ""}`}>{children}</td>;
}
